import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { ArrowTopRightOnSquareIcon, CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/outline";

import useLeads from "../../../data/useLeads";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import Modal from "../../../layouts/Modal";
import { ILead } from "../../../models/Lead";
import PriceFormat from "../../../utils/priceFormat";
import usePricingAccess from "../../../hooks/usePricingAccess";
import useNotification from "../../notifications/useNotifications";
import { ButtonInput } from "../../shared/Inputs/ButtonInput";
import LoadingWheel from "../../shared/LoadingWheel";

interface IBulkResponse {
  data: string,
  message: string,
  success: boolean
}

const fakeResponse: IBulkResponse[] = [
  {
    data: "d166104f-c088-47e7-172b-08db1029e6e8",
    message: "Completed Lead Successfully!",
    success: true
  },
  {
    data: "d166104f-c088-47e7-172b-08db1029e6e8",
    message: "Unable to complete COD. \nPlease check all the requirements for this lead and try again.",
    success: false
  },
  {
    data: "d166104f-c088-47e7-172b-08db1029e6e8",
    message: "Completed Lead Successfully!",
    success: true
  },
  {
    data: "d166104f-c088-47e7-172b-08db1029e6e8",
    message: "Completed Lead Successfully!",
    success: true
  },
]

export default function BulkCompleteLeads({ table, open, setOpen }: { table: any, open: boolean, setOpen: any }) {
  const { t } = useTranslation("leads");
  const { canViewPrices } = usePricingAccess();
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const { saveHandlers } = useDefaultCRUDHandlers("Leads");
  const { bulkComplete, bulkSubmitCod } = useLeads(true, "BulkCompleteLeads");
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<IBulkResponse[] | null>(null);
  const [leadsSelected, setLeadsSelected] = useState<string[]>([]);
  const [actionType, setActionType] = useState<'complete' | 'cod'>('complete');
  const [leads, setLeads] = useState<ILead[]>([]);
  const [actionedLeads, setActionedLeads] = useState<ILead[]>([]);

  const handleBulkAction = () => {
    const body = {
      leads: leadsSelected
    };
    setActionedLeads(leads.filter((lead: ILead) => leadsSelected.includes(lead.id)));

    setLoading(true);

    const mutation = actionType === 'complete' ? bulkComplete : bulkSubmitCod;

    mutation.mutate(body, {
      onSuccess: (res: any) => {
        setResponse(res);
        saveHandlers.onSuccess();
        table.resetRowSelection();
        setLeadsSelected([]);
      },
      onError: (res: any) => {
        addNotification({
          variant: "error",
          primaryText: res.message,
        })
      },
      onSettled: () => {
        setLoading(false);
      }
    });
  }

  const handleSelected = (id: string) => {
    if (leadsSelected.includes(id)) {
      setLeadsSelected(leadsSelected.filter((leadId: string) => leadId !== id));
    } else {
      setLeadsSelected([...leadsSelected, id]);
    }
  }

  useEffect(() => {
    if (open) {
      // Snapshot the selected rows when the modal opens so later table
      // refreshes don't mutate the list or wipe the user's selections.
      setLeads(
        table
          ? table.getSelectedRowModel().flatRows.map((row: any) => row.original)
          : []
      );
    } else {
      setResponse(null);
      setLeadsSelected([]);
      setActionType('complete');
      setLeads([]);
    }
  }, [open]);

  return (
    <Modal width="max-w-4xl" open={open} setOpen={setOpen} >
      <h1 className="text-xl">{t("Bulk Lead Actions")}</h1>
      <hr />

      {response ?
        (
          <div className="mt-4">
            <p>
              {actionType === 'complete'
                ? t("Tried to complete {{count}} lead(s).", { count: response.length })
                : t("Tried to submit COD for {{count}} lead(s).", { count: response.length })}
            </p>
            <br />

            <div className="flex flex-col gap-y-2 divide-y">
              {response.map((res: IBulkResponse) => {
                const foundLead = actionedLeads.find(lead => lead.id === res.data);

                return (
                  <div className="flex justify-between pt-2">
                    <div className="flex grow items-center gap-x-2">
                      {res.success ? <CheckCircleIcon className="text-green-500 w-7 h-7" /> : <XCircleIcon className="text-red-500 w-7 h-7" />}
                      <p className="whitespace-pre-line">{res.message}</p>
                    </div>
                    <Link
                      to={`/leads/${res.data}`} target="_blank" rel="noopener noreferrer"
                      className="underline text-blue-500 hover:text-blue-700"
                    >
                      {foundLead?.contact?.fullname} - {foundLead?.vehicle?.vrm} <ArrowTopRightOnSquareIcon className="inline-block mb-1 w-4 h-4" />
                    </Link>
                  </div>
                )
              })}
            </div>
            <div className="flex justify-end">
              <ButtonInput label={t("Reset")} isSubmit={false} onClick={() => setResponse(null)} classes={"mt-6"} />
            </div>
          </div>
        )
        :
        (
          loading ?
            (
              <div className="mt-4">
                <LoadingWheel />
              </div>
            )
            :
            (
              <div className="mt-4">
                <div className="flex gap-x-4 mb-4">
                  <button
                    className={`px-4 py-2 rounded-md ${actionType === 'complete' ? 'bg-primary-600 text-white' : 'bg-gray-200'}`}
                    onClick={() => setActionType('complete')}
                  >
                    {t("Complete Leads")}
                  </button>
                  <button
                    className={`px-4 py-2 rounded-md ${actionType === 'cod' ? 'bg-primary-600 text-white' : 'bg-gray-200'}`}
                    onClick={() => setActionType('cod')}
                  >
                    {t("Submit COD")}
                  </button>
                </div>

                <p>
                  {actionType === 'complete'
                    ? t("Are you sure you want to complete all of these leads?")
                    : t("Are you sure you want to submit COD for all of these leads?")}
                </p>
                <p className="font-semibold">
                  {actionType === 'complete'
                    ? t("This will attempt to COD and raise payments for all of these leads!")
                    : t("This will attempt to submit COD for all of these leads!")}
                </p>
                <br />
                <p>
                  {actionType === 'complete'
                    ? t("For confirmation, please select all the leads you want to complete.")
                    : t("For confirmation, please select all the leads you want to submit COD for.")}
                </p>
                <p className="font-semibold">{t("{{count}} lead(s) selected.", { count: leadsSelected.length })}</p>
                <br />

                {/* Table of selected leads */}
                <div className="space-y-2 divide-y">
                  {leads.length > 0 &&
                    leads.map((lead: ILead) =>
                      <div key={lead.id} className="grid grid-cols-6 items-center">
                        <img
                          className="w-24 h-12 object-contain"
                          src={`${lead.vehicle != null ? lead.vehicle.vehicleImageUrl : undefined}`}
                        />
                        <span>{lead.contact?.fullname}</span>
                        <span>{lead.vehicle?.vrm}</span>
                        <span>{lead.vehicle?.postcode}</span>
                        <span>{canViewPrices ? PriceFormat(lead.price) : ""}</span>
                        <input type="checkbox" onChange={() => handleSelected(lead.id)} />
                      </div>
                    )
                  }
                </div>

                <ButtonInput
                  disabled={leadsSelected.length < 1}
                  label={actionType === 'complete' ? t("Complete Leads") : t("Submit COD")}
                  isSubmit={false}
                  onClick={() => handleBulkAction()}
                  classes={"mt-6"}
                />
              </div>
            )
        )}

    </Modal >
  )
}